import React, { useEffect, useState } from 'react';

/**
 * Auto-cycling product demo for the Landing hero. 4 real screenshots from
 * the running app, crossfading every 4 seconds, wrapped in a mac-style
 * browser chrome to make the visual feel like a live walkthrough.
 *
 * Pause-on-hover is built in so a curious visitor can hover to read.
 */
const FRAMES = [
  {
    src: '/demo/01-dashboard.jpg',
    caption: 'Real-time hiring metrics',
    sub: 'See your pipeline at a glance',
  },
  {
    src: '/demo/02-command-center.jpg',
    caption: 'Drag-and-drop pipeline',
    sub: 'Single-screen Recruiter Command Center',
  },
  {
    src: '/demo/03-best10.jpg',
    caption: '"Best 10" AI shortlist',
    sub: 'Find the right candidate in seconds',
  },
  {
    src: '/demo/04-settings.jpg',
    caption: 'Multi-tenant SaaS',
    sub: 'Plan limits, team management, support tickets',
  },
];

const FRAME_MS = 4200;

const LandingHeroDemo = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setActive((i) => (i + 1) % FRAMES.length);
    }, FRAME_MS);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="landing-hero-demo"
    >
      {/* Glow */}
      <div className="absolute -inset-4 bg-gradient-to-r from-teal-500/20 to-emerald-500/20 blur-3xl rounded-3xl" />

      {/* Browser chrome */}
      <div className="relative bg-slate-900 border border-slate-800 rounded-xl shadow-2xl overflow-hidden">
        {/* Title bar */}
        <div className="h-9 px-3 flex items-center gap-2 bg-slate-950/70 border-b border-slate-800">
          <span className="w-2.5 h-2.5 rounded-full bg-rose-500/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
          <div className="flex-1 flex items-center justify-center">
            <span className="text-[10px] font-mono text-slate-400 truncate max-w-xs">
              app.interviewwala.in
              <span className="text-slate-600">
                {FRAMES[active].src.replace('/demo/', '/').replace('.jpg', '')}
              </span>
            </span>
          </div>
          <span className="w-12" />
        </div>

        {/* Frames */}
        <div className="relative bg-white aspect-[16/10]">
          {FRAMES.map((f, i) => (
            <img
              key={f.src}
              src={f.src}
              alt={f.caption}
              loading={i === 0 ? 'eager' : 'lazy'}
              className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 ease-in-out ${
                i === active ? 'opacity-100' : 'opacity-0'
              }`}
              data-testid={`landing-hero-demo-frame-${i}`}
            />
          ))}

          {/* Caption overlay */}
          <div className="absolute bottom-0 inset-x-0 px-4 py-3 bg-gradient-to-t from-slate-950/85 via-slate-950/60 to-transparent">
            <p
              className="text-sm font-bold text-white leading-tight transition-opacity duration-500"
              key={`cap-${active}`}
            >
              {FRAMES[active].caption}
            </p>
            <p className="text-[11px] text-slate-300 leading-tight">
              {FRAMES[active].sub}
            </p>
          </div>
        </div>

        {/* Indicators */}
        <div className="h-9 px-3 flex items-center justify-center gap-1.5 bg-slate-950/70 border-t border-slate-800">
          {FRAMES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              className={`h-1.5 rounded-full transition-all ${
                i === active
                  ? 'w-6 bg-teal-400'
                  : 'w-1.5 bg-slate-700 hover:bg-slate-500'
              }`}
              aria-label={`Show frame ${i + 1}`}
              data-testid={`landing-hero-demo-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LandingHeroDemo;
